import type * as React from "react";
import type { Omittable } from "../../lib/props";

/**
 * Circular progress glyph: an outlined circle with a pie-slice fill, plus
 * optional terminal-state overlays (check / x) for 100%-with-meaning states.
 * Entity-agnostic — the consumer resolves what the percentage, color, and
 * overlay mean (e.g. a workflow stage's progress and done/canceled types).
 * `color` must be a concrete paintable value (resolved token or CSS string):
 * SVG presentation attributes cannot read var() indirection reliably across
 * the places this renders (inline glyphs, canvas-exported charts).
 */

// Overlay icon paths, authored on a 24x24 viewBox.
const OVERLAY_PATHS = {
  check:
    "M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z",
  x: "M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z",
} as const;

export type ProgressPieOverlay = keyof typeof OVERLAY_PATHS;

export function progressPiePath(
  cx: number,
  cy: number,
  r: number,
  percentage: number,
): string {
  if (percentage >= 100) return "";
  const angle = (Math.min(100, Math.max(0, percentage)) / 100) * 360;
  const rad = ((angle - 90) * Math.PI) / 180;
  const x = cx + r * Math.cos(rad);
  const y = cy + r * Math.sin(rad);
  const largeArc = angle > 180 ? 1 : 0;
  return `M${cx},${cy} L${cx},${cy - r} A${r},${r} 0 ${largeArc} 1 ${x},${y} Z`;
}

export type ProgressPieProps = Omittable<{
  /** Fill percentage, clamped to [0, 100]. */
  percentage: number;
  /** Concrete paintable color (resolved token rgb/hex or CSS color string). */
  color: string;
  /** Edge length in px. */
  size: number;
  /** Terminal-state glyph drawn over the filled pie. */
  overlay?: ProgressPieOverlay;
}>;

export function ProgressPie({
  percentage,
  color,
  size,
  overlay,
}: ProgressPieProps): React.ReactElement {
  const cx = size / 2;
  const cy = size / 2;
  const outlineR = size / 2 - 1;
  const gap = 1.5;
  const fillR = outlineR - gap;
  const pct = Math.min(100, Math.max(0, percentage));

  const overlayScale = 0.33;
  const overlaySize = size * overlayScale;
  const overlayOffset = (size - overlaySize) / 2;
  const overlayScaleFactor = overlaySize / 24;
  const overlayPath = overlay ? OVERLAY_PATHS[overlay] : undefined;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      data-slot="progress-pie"
    >
      {pct >= 100 ? (
        <circle cx={cx} cy={cy} r={fillR} fill={color} />
      ) : pct > 0 ? (
        <path d={progressPiePath(cx, cy, fillR, pct)} fill={color} />
      ) : null}
      <circle
        cx={cx}
        cy={cy}
        r={outlineR}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
      />
      {overlayPath && (
        <path
          d={overlayPath}
          fill="white"
          transform={`translate(${overlayOffset}, ${overlayOffset}) scale(${overlayScaleFactor})`}
        />
      )}
    </svg>
  );
}
